/**
 * Helpers for FL-209 (label truncation, hover-tooltip wrapping, student
 * ordering by team) and FL-215 (above-the-bar 2-line clamp via
 * `friendlyLabel`).
 */
import { Analysis, Team, User } from "../../types";

/**
 * FL-238: natural, case-insensitive string comparison so chart bars sort
 * "Team 2" before "Team 10" (numeric-aware) rather than lexicographically.
 * Used as the single comparator for all bar-chart ordering.
 */
export function naturalCompare(a: string | undefined, b: string | undefined): number {
  return (a || "").localeCompare(b || "", undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

/**
 * FL-238: sort an array of Analysis objects alphabetically (natural) by their
 * label — used to order question bars in the per-question and released charts.
 */
export function sortAnalysesByLabel(analyses: Analysis[]): Analysis[] {
  return [...analyses].sort((a, b) => naturalCompare(a.label, b.label));
}

/**
 * Build a map from userId → team name based on group membership.
 * If a user belongs to multiple teams, the first team encountered wins.
 */
export function buildUserTeamMap(teams: Team[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const team of teams) {
    for (const userId of team.userIds || []) {
      if (!map.has(userId)) {
        map.set(userId, team.name);
      }
    }
  }
  return map;
}

/**
 * Sort users by team (alphabetically by team name) then by firstName within team.
 * Users without a team are placed at the end (sorted by firstName).
 */
export function sortUsersByTeamThenName(users: User[], teams: Team[]): User[] {
  const userTeamMap = buildUserTeamMap(teams);
  return [...users].sort((a, b) => {
    const teamA = userTeamMap.get(a.id);
    const teamB = userTeamMap.get(b.id);
    // Users without a team go to the end
    if (!teamA && teamB) return 1;
    if (teamA && !teamB) return -1;
    if (teamA && teamB && teamA !== teamB) {
      return naturalCompare(teamA, teamB);
    }
    // Same team (or both teamless) → sort by firstName (natural)
    return naturalCompare(a.firstName, b.firstName);
  });
}

/**
 * Sort an array of Analysis objects by their user's team (alphabetical by team
 * name) then by the user's firstName within team. Users without a team go last.
 *
 * Used to group rows by team in the Averages-style results tables.
 */
export function sortAnalysesByTeamThenName(
  analyses: Analysis[],
  teams: Team[],
): Analysis[] {
  const userTeamMap = buildUserTeamMap(teams);
  return [...analyses].sort((a, b) => {
    const teamA = userTeamMap.get(a.user.id);
    const teamB = userTeamMap.get(b.user.id);
    if (!teamA && teamB) return 1;
    if (teamA && !teamB) return -1;
    if (teamA && teamB && teamA !== teamB) {
      return naturalCompare(teamA, teamB);
    }
    return naturalCompare(a.user.firstName, b.user.firstName);
  });
}

/**
 * FL-238: sort Analysis rows by team (alphabetical, natural) then by the
 * displayed LABEL (the student's full name) within team. Teamless rows go last.
 *
 * Chart-facing variant of sortAnalysesByTeamThenName. It keys on `label` rather
 * than `user.firstName` because in real LMS data the User's first_name column is
 * often blank (the display name is synced from the LTI profile into the analysis
 * label), which made the firstName-based sort a silent no-op — student bars
 * rendered in raw backend order. `label` is always populated and matches exactly
 * what the user sees on each bar.
 */
export function sortAnalysesByTeamThenLabel(
  analyses: Analysis[],
  teams: Team[],
): Analysis[] {
  const userTeamMap = buildUserTeamMap(teams);
  return [...analyses].sort((a, b) => {
    const teamA = userTeamMap.get(a.user.id);
    const teamB = userTeamMap.get(b.user.id);
    if (!teamA && teamB) return 1;
    if (teamA && !teamB) return -1;
    if (teamA && teamB && teamA !== teamB) {
      return naturalCompare(teamA, teamB);
    }
    return naturalCompare(a.label, b.label);
  });
}

/**
 * Wrap a long label into an array of lines suitable for use as VictoryTooltip
 * text. Each line is at most `maxCharsPerLine` chars (word-boundary wrap).
 * An optional trailing line is appended after the wrapped label (typically a
 * score summary like "(Received: 4.0)").
 *
 * Used to prevent the chart hover tooltip from overflowing the viewport with
 * long question prompts.
 */
export function wrapLabelForTooltip(
  label: string,
  trailingLine?: string,
  maxCharsPerLine = 40,
): string[] {
  const lines: string[] = [];
  const words = (label || "").split(/\s+/).filter(Boolean);
  let current = "";
  for (const word of words) {
    if (word.length > maxCharsPerLine) {
      // Force-break a single oversized word with hyphenation.
      if (current) {
        lines.push(current);
        current = "";
      }
      let i = 0;
      while (i < word.length) {
        const chunk = word.slice(i, i + maxCharsPerLine);
        i += maxCharsPerLine;
        if (i < word.length) {
          lines.push(chunk + "-");
        } else {
          current = chunk;
        }
      }
      continue;
    }
    if (current.length === 0) {
      current = word;
    } else if (current.length + 1 + word.length <= maxCharsPerLine) {
      current += " " + word;
    } else {
      lines.push(current);
      current = word;
    }
  }
  if (current) lines.push(current);
  if (trailingLine) lines.push(trailingLine);
  return lines;
}

/**
 * Compute a Victory tooltip flyout width that fits the longest line of a
 * (possibly multi-line) label. Estimates ~7px per char + 24px for padding;
 * Victory's auto-sizing underestimates multi-line widths, causing the first
 * line to bleed past the flyout edge.
 */
export function tooltipFlyoutWidth(
  label: string | string[] | undefined,
): number {
  const lines = Array.isArray(label) ? label : [String(label ?? "")];
  const maxLen = Math.max(0, ...lines.map((l) => l.length));
  return Math.max(60, maxLen * 7 + 24);
}

/**
 * Wrap text into lines no longer than `targetLength` characters by walking
 * word-by-word. Words longer than the target are hyphen-broken via
 * `chunkString`. Used by `friendlyLabel` to lay out chart axis labels.
 */
export function divideIntoLines(text: string, targetLength: number): string[] {
  const words = text.split(" ");
  const lines: string[] = [];
  let line = "";
  for (let i = 0; i < words.length; i++) {
    if (words[i].length > targetLength) {
      if (line.trim()) {
        lines.push(line.trim());
        line = "";
      }
      const chunks = chunkString(words[i], targetLength);
      for (let j = 0; j < chunks.length; j++) {
        lines.push(chunks[j]);
      }
    } else if (line.length + words[i].length + 1 <= targetLength) {
      line += words[i] + " ";
    } else {
      lines.push(line.trim());
      line = words[i] + " ";
    }
  }
  const finalLine = line.trim();
  if (finalLine) lines.push(finalLine);
  return lines;
}

/**
 * Split a single oversized word into hyphenated chunks of `length` chars
 * (the trailing chunk has no hyphen). Helper for `divideIntoLines`.
 */
export function chunkString(str: string, length: number): string[] {
  const chunks: string[] = [];
  for (let i = 0; i < str.length; i += length) {
    chunks.push(str.substring(i, i + length) + "-");
  }
  if (chunks.length > 0) {
    chunks[chunks.length - 1] = chunks[chunks.length - 1].slice(0, -1);
  }
  return chunks;
}

/**
 * FL-215: format a question label for display directly above a chart bar.
 * Clamps the visible text to at most 2 lines (~70 chars per line, sized
 * to use most of the chart's horizontal width at the FL-215 14px font)
 * and appends an ellipsis when the input would have spilled over. The
 * full label is still surfaced via the FL-209 hover flyout, so nothing
 * is permanently hidden.
 */
export function friendlyLabel(
  text: string,
  charsPerLine = 70,
  maxLines = 2,
): string[] {
  const truncated = truncateAtWordBoundary(text, charsPerLine * maxLines);
  const lines = divideIntoLines(truncated, charsPerLine);
  if (lines.length > maxLines) {
    const last = lines[maxLines - 1];
    lines[maxLines - 1] = last.endsWith("…") ? last : last + "…";
    return lines.slice(0, maxLines);
  }
  return lines;
}

/**
 * Truncate text at a word boundary near the given character limit.
 * If the text is already short enough, returns it unchanged.
 * Otherwise, returns the longest word-boundary prefix that fits within `maxChars`,
 * suffixed with "…".
 *
 * Example: truncateAtWordBoundary("How effectively did the team collaborate?", 20)
 *   → "How effectively did…"
 */
export function truncateAtWordBoundary(text: string, maxChars: number): string {
  if (!text || text.length <= maxChars) return text;
  const ellipsis = "…";
  const budget = maxChars - ellipsis.length;
  if (budget <= 0) return ellipsis;

  // Walk word by word, accumulating the longest run that fits in `budget`.
  // Splitting with a capturing group keeps the whitespace separators so the
  // running length stays accurate.
  const tokens = text.split(/(\s+)/);
  let result = "";
  for (const token of tokens) {
    if ((result + token).length > budget) break;
    result += token;
  }
  result = result.trimEnd();
  if (!result) {
    // First word longer than budget — fall back to hard truncation.
    return text.slice(0, budget) + ellipsis;
  }
  return result + ellipsis;
}
